/**
 * copilotWriteConfirmation — fonctions pures de la confirmation d'écriture du
 * copilote (UPT-2016, lot 2). Contrat tel que livré par le rest
 * (`Geode-specs/reports/UPT-2016-L2.md` §3) :
 *
 *  1. Quand le modèle appelle un outil d'écriture (`geode_record_create`,
 *     `geode_record_update`, `geode_action`), RIEN n'est écrit : le serveur émet
 *     l'événement SSE `write_confirmation`
 *     `{ type: 'write-confirmation', requestId, tool, entity, operation, recordId,
 *        payload, summary }`.
 *  2. Confirmer ou Annuler = une NOUVELLE requête `POST /api/v1/ai/copilot/stream`
 *     `{ message, conversationId, pageContext, confirmWrite: { requestId, confirmed } }` ;
 *     le serveur répond par l'événement `write_result`
 *     `{ requestId, entity, operation, recordId, status, error? }` avec
 *     `status` ∈ EXECUTED | FAILED | CANCELLED | EXPIRED.
 *  3. Seul ce clic écrit : `confirmed: true` dans les arguments d'un outil n'a
 *     aucun effet, le modèle ne peut pas se confirmer lui-même.
 *
 * Tout ce qui vient du flux est de la donnée non fiable : l'analyse est
 * défensive (null si l'événement est illisible, rien n'est alors proposé à
 * l'utilisateur — et rien n'est écrit).
 */

export type WriteOperation = 'CREATE' | 'UPDATE' | 'ACTION';

/** Une demande d'écriture en attente du clic de l'utilisateur. */
export type WriteConfirmation = {
  requestId: string;
  tool: string;
  entity: string;
  operation: WriteOperation;
  /** Identifiant visé (mise à jour, action) ; null pour une création. */
  recordId: string | null;
  /** Données (CREATE), champs modifiés (UPDATE), `{ action, params }` (ACTION). */
  payload: unknown;
  /** Résumé lisible, composé par le serveur. */
  summary: string;
};

export type WriteResultStatus = 'EXECUTED' | 'FAILED' | 'CANCELLED' | 'EXPIRED';

/** Issue d'une demande après le clic (événement `write_result`). */
export type WriteResult = {
  requestId: string;
  entity: string;
  operation: WriteOperation | null;
  recordId: string | null;
  status: WriteResultStatus;
  /** Message court de l'échec (statut FAILED). */
  error: string | null;
};

/** Corps du champ `confirmWrite` de la requête de flux. */
export type ConfirmWriteBody = { requestId: string; confirmed: boolean };

const OPERATIONS: WriteOperation[] = ['CREATE', 'UPDATE', 'ACTION'];
const RESULT_STATUSES: WriteResultStatus[] = ['EXECUTED', 'FAILED', 'CANCELLED', 'EXPIRED'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** Un identifiant d'enregistrement peut arriver en nombre ou en texte. */
const asNullableId = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const text = asText(value);
  return text === '' ? null : text;
};

const asOperation = (value: unknown): WriteOperation | null => {
  const text = asText(value).toUpperCase();
  return (OPERATIONS as string[]).includes(text) ? (text as WriteOperation) : null;
};

const parseJsonRecord = (raw: string): Record<string, unknown> | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Données de l'événement `write_confirmation` -> demande à confirmer, ou null
 * quand l'événement est illisible ou sans identifiant de demande (sans
 * `requestId` le clic ne pourrait rien confirmer).
 */
export const parseWriteConfirmation = (raw: string): WriteConfirmation | null => {
  const record = parseJsonRecord(raw);
  if (record === null) return null;
  const requestId = asText(record.requestId);
  const operation = asOperation(record.operation);
  if (requestId === '' || operation === null) return null;
  const entity = asText(record.entity);
  const summary = asText(record.summary);
  return {
    requestId,
    tool: asText(record.tool),
    entity,
    operation,
    recordId: asNullableId(record.recordId),
    payload: record.payload ?? null,
    summary: summary !== '' ? summary : [operation, entity].filter((part) => part !== '').join(' '),
  };
};

/** Données de l'événement `write_result` -> issue de la demande, ou null si illisible. */
export const parseWriteResult = (raw: string): WriteResult | null => {
  const record = parseJsonRecord(raw);
  if (record === null) return null;
  const status = asText(record.status).toUpperCase();
  if (!(RESULT_STATUSES as string[]).includes(status)) return null;
  const error = asText(record.error);
  return {
    requestId: asText(record.requestId),
    entity: asText(record.entity),
    operation: asOperation(record.operation),
    recordId: asNullableId(record.recordId),
    status: status as WriteResultStatus,
    error: error === '' ? null : error,
  };
};

/** Champ `confirmWrite` de la requête de flux : le clic de l'utilisateur, rien d'autre. */
export const buildConfirmWrite = (requestId: string, confirmed: boolean): ConfirmWriteBody => ({
  requestId,
  confirmed,
});

/**
 * Corps de la requête de flux. `confirmWrite` n'est présent QUE pour une réponse à
 * une carte de confirmation (Confirmer / Annuler) ; une question ordinaire n'en
 * porte jamais, donc ne peut rien écrire.
 */
export const buildStreamBody = (params: {
  message: string;
  conversationId: string | null;
  pageContext: string;
  confirmWrite?: ConfirmWriteBody;
}): Record<string, unknown> => ({
  message: params.message,
  conversationId: params.conversationId,
  pageContext: params.pageContext,
  ...(params.confirmWrite ? { confirmWrite: params.confirmWrite } : {}),
});

export type WriteResultTone = 'success' | 'danger' | 'warning' | 'neutral';

/** Couleur d'ÉTAT de l'issue (jamais la marque du locataire). */
export const writeResultTone = (status: WriteResultStatus): WriteResultTone => {
  switch (status) {
    case 'EXECUTED':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'EXPIRED':
      return 'warning';
    default:
      return 'neutral';
  }
};

/** Clé i18n du message d'issue (accesseur au niveau module : pas de clé composée dans le JSX). */
export const writeResultLabelKey = (status: WriteResultStatus): string => {
  switch (status) {
    case 'EXECUTED':
      return 'copilot.writeConfirmation.result.executed';
    case 'FAILED':
      return 'copilot.writeConfirmation.result.failed';
    case 'EXPIRED':
      return 'copilot.writeConfirmation.result.expired';
    default:
      return 'copilot.writeConfirmation.result.cancelled';
  }
};
